import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import type { CallAnalysisTagDefinitionChoice } from '@reach/shared-types/call-analysis-tag-definitions';
import { Button } from '../../../components/ui/button';
import { ColorFieldRow } from './ColorFieldRow';
import { IconPicker } from './IconPicker';
import { choicePillInlineStyles } from './tag-choice-pill-utils';
import { resolveTagIcon } from './tag-icon-utils';
import styles from './TagDefinitionChoiceCard.module.css';

export interface TagDefinitionChoiceCardProps {
  choice: CallAnalysisTagDefinitionChoice;
  previewChoice: CallAnalysisTagDefinitionChoice;
  isEditing: boolean;
  onStartEdit: () => void;
  onRemove: () => void;
  editBackgroundInputId: string;
  editTextColorInputId: string;
  editIconPickerId: string;
  editBackgroundValue: string;
  editTextColorValue: string;
  editIconValue: string;
  onEditBackgroundChange: (value: string) => void;
  onEditTextColorChange: (value: string) => void;
  onEditIconChange: (name: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
}

export const TagDefinitionChoiceCard: React.FC<TagDefinitionChoiceCardProps> = ({
  choice,
  previewChoice,
  isEditing,
  onStartEdit,
  onRemove,
  editBackgroundInputId,
  editTextColorInputId,
  editIconPickerId,
  editBackgroundValue,
  editTextColorValue,
  editIconValue,
  onEditBackgroundChange,
  onEditTextColorChange,
  onEditIconChange,
  onCancelEdit,
  onSaveEdit,
}) => {
  const ResolvedIcon = previewChoice.icon ? resolveTagIcon(previewChoice.icon) : null;
  const pillStyle = choicePillInlineStyles(previewChoice.color, previewChoice.textColor);

  return (
    <div className={styles.choiceCard}>
      <div className={styles.choiceCardRow}>
        <div className={styles.choicePillWrap}>
          <span className={styles.choicePill} style={pillStyle} title={previewChoice.value}>
            {ResolvedIcon ? (
              <span className={styles.choicePillIcon} title={previewChoice.icon}>
                <ResolvedIcon aria-hidden />
              </span>
            ) : null}
            <span className={styles.choicePillLabel}>{previewChoice.value}</span>
          </span>
        </div>
        <div className={styles.choiceActions}>
          {!isEditing ? (
            <button
              type="button"
              onClick={onStartEdit}
              className={styles.choiceEditButton}
              aria-label={`Edit ${choice.value}`}>
              <Pencil />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onRemove}
            className={styles.choiceRemoveButton}
            aria-label={`Remove ${choice.value}`}>
            <Trash2 />
          </button>
        </div>
      </div>
      {isEditing ? (
        <div className={styles.choiceEditPanel}>
          <p className={styles.choiceReadonlyValue}>
            Value: <code>{choice.value}</code>
          </p>
          <ColorFieldRow
            label="Background"
            colorValue={editBackgroundValue}
            onColorValueChange={onEditBackgroundChange}
            inputId={editBackgroundInputId}
          />
          <ColorFieldRow
            label="Text"
            colorValue={editTextColorValue}
            onColorValueChange={onEditTextColorChange}
            inputId={editTextColorInputId}
          />
          <IconPicker value={editIconValue} onChange={onEditIconChange} id={editIconPickerId} />
          <div className={styles.choiceEditActions}>
            <Button type="button" variant="outline" size="sm" onClick={onCancelEdit}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={onSaveEdit}>
              Save choice
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
